import React, { useCallback, useEffect, useState } from "react";
import { CalendarClock, Send, Save } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

export default function StatusPackSchedule({ clientId }) {
  const [state, setState] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [frequency, setFrequency] = useState("monthly");
  const [recipients, setRecipients] = useState("");
  const [includeCertificate, setIncludeCertificate] = useState(true);
  const [includeModelRisk, setIncludeModelRisk] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/enterprise360/clients/${clientId}/status-pack-schedule`);
      setState(data);
      setEnabled(data.schedule.enabled);
      setFrequency(data.schedule.frequency);
      setRecipients((data.schedule.recipients || []).join(", "));
      setIncludeCertificate(data.schedule.include_certificate !== false);
      setIncludeModelRisk(data.schedule.include_model_risk !== false);
    } catch { /* non-blocking */ }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const recips = recipients.split(",").map((r) => r.trim()).filter(Boolean);
      const { data } = await api.put(`/admin/enterprise360/clients/${clientId}/status-pack-schedule`,
        { enabled, frequency, recipients: recips, include_certificate: includeCertificate, include_model_risk: includeModelRisk });
      setState(data); toast.success("Schedule saved");
    } catch { toast.error("Could not save schedule"); } finally { setBusy(false); }
  };
  const sendNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/enterprise360/clients/${clientId}/status-pack-schedule/send-now`);
      data.sent ? toast.success("Status pack emailed") : toast.error("Nothing sent (check recipients / email settings)");
      load();
    } catch { toast.error("Send failed"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="status-pack-schedule" className="border border-zinc-200 rounded-lg p-4 lg:col-span-2">
      <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[var(--e360-primary)] inline-flex items-center gap-1 mb-2"><CalendarClock size={13} /> Status pack auto-send</div>
      {!state ? <div className="text-sm text-zinc-400 py-3 text-center">Loading…</div> : !state.entitled ? (
        <p data-testid="sps-not-entitled" className="text-[12px] text-zinc-500 bg-zinc-50 border border-zinc-200 rounded px-3 py-2">
          Scheduled status packs are not enabled for this workspace. Ask your administrator to enable the <code>status_pack_schedule</code> entitlement.
        </p>
      ) : (
        <>
          <p className="text-[12px] text-zinc-500 mb-3">Automatically email the Delivery Status Pack to the engagement recipients on a cadence — no manual sending.</p>
          <div className="flex flex-wrap items-end gap-4">
            <label className="flex items-center gap-2 text-[13px] text-zinc-700">
              <input data-testid="sps-enabled-toggle" type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="w-4 h-4" />
              Enabled
            </label>
            <div>
              <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-400 mb-1">Cadence</div>
              <select data-testid="sps-frequency" value={frequency} onChange={(e) => setFrequency(e.target.value)}
                className="border border-zinc-300 rounded px-2 py-1.5 text-[13px]">
                {(state.frequencies || []).map((f) => <option key={f} value={f}>{f[0].toUpperCase() + f.slice(1)}</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-400 mb-1">Recipients (comma-separated)</div>
              <input data-testid="sps-recipients" value={recipients} onChange={(e) => setRecipients(e.target.value)}
                placeholder={(state.default_recipients || []).join(", ") || "consultant@firm.com"}
                className="w-full border border-zinc-300 rounded px-2 py-1.5 text-[13px]" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-[13px] text-zinc-700 mt-3">
            <input data-testid="sps-include-certificate" type="checkbox" checked={includeCertificate}
              onChange={(e) => setIncludeCertificate(e.target.checked)} className="w-4 h-4" />
            Attach the Assurance Certificate to each scheduled send
          </label>
          <label className="flex items-center gap-2 text-[13px] text-zinc-700 mt-2">
            <input data-testid="sps-include-model-risk" type="checkbox" checked={includeModelRisk}
              onChange={(e) => setIncludeModelRisk(e.target.checked)} className="w-4 h-4" />
            Include the board model-risk posture &amp; open alerts
          </label>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <button data-testid="sps-save" onClick={save} disabled={busy}
              className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.12em] uppercase text-white bg-[var(--e360-primary)] px-3 py-1.5 rounded hover:opacity-90 disabled:opacity-40"><Save size={13} /> Save</button>
            <button data-testid="sps-send-now" onClick={sendNow} disabled={busy}
              className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.12em] uppercase text-[var(--e360-primary)] border border-[var(--e360-primary)] px-3 py-1.5 rounded hover:bg-[var(--e360-primary)]/5 disabled:opacity-40"><Send size={13} /> Send now</button>
            {state.schedule?.enabled && state.next_due && (
              <span data-testid="sps-next-due" className="text-[11px] text-zinc-400">Next send: {new Date(state.next_due).toLocaleDateString("en-GB")}</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
